/**
 * web/src/pages/Confirmations.tsx
 * UI-07 + UI-10: Tracked-email list with PENDING / IN_CONVERSATION /
 * CONFIRMED status, overdue indicator, and Mark Confirmed action.
 *
 * Tabs: Pending (default), Confirmed, All. The status filter is applied
 * client-side from the cache (the data layer fetches all statuses for
 * the active event); this avoids re-fetching on tab switch. Per Phase
 * 6.3 D-POLICY-03, the Pending tab includes both PENDING and the new
 * IN_CONVERSATION rows so reviewers see ambiguous classifications
 * surfaced alongside un-replied emails.
 *
 * IN_CONVERSATION rows (Phase 6.3 UI-10):
 *   - Render an amber "Needs Manual Review" badge.
 *   - Show lastClassification.reason as a "Classifier: ..." caption.
 *   - Keep the "Mark confirmed" button as the manual-fallback path
 *     for ambiguous classifications.
 *
 * Overdue check (UI-07 + CONTEXT.md D-1.4): TrackedEmail.followUpDueAt
 * is ISO; isAfter(now, dueAt) shows a red overdue pill. Extended in
 * Phase 6.3 to also apply on IN_CONVERSATION rows so a stalled
 * ambiguous conversation surfaces the overdue treatment.
 *
 * Field mapping (Rule 1 fix vs PLAN.md example):
 *   - PLAN referenced `e.recipient` / `e.sentAt` / `e.emailType`; actual
 *     prisma columns (web/src/api/types.ts) are `recipientEmail`,
 *     `createdAt` (sent-time proxy), and `stepId` (welcome_email,
 *     ops_next_steps).
 */
import { useMemo, useState } from 'react';
import { format, formatDistanceToNow, isAfter } from 'date-fns';
import { useTrackedEmails } from '../hooks/queries/useTrackedEmails';
import { useMarkConfirmed } from '../hooks/mutations/useMarkConfirmed';
import { useGmailPoll } from '../hooks/mutations/useGmailPoll';
import { useMe } from '../hooks/queries/useMe';
import { useEvent } from '../hooks/useEvent';
import { useSetTopbar } from '../components/layout/Topbar';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { RowCard } from '../components/shared/RowCard';
import { Dot } from '../components/shared/Dot';
import { Button } from '../components/shared/Button';
import { Empty } from '../components/shared/Empty';
import { Skeleton } from '../components/shared/Skeleton';
import { Icon } from '../lib/icons';
import { titleCase } from '../lib/format';
import type { TrackedEmail } from '../api/types';

/** Active tab. Default 'pending'. */
type Tab = 'pending' | 'confirmed' | 'all';

/**
 * Confirmations list page (UI-07). Renders tracked emails for the active
 * event partitioned across Pending / Confirmed / All tabs with an inline
 * Mark Confirmed action that mutates optimistically (D-2.2).
 *
 * @sideEffects React Query subscription on ['tracked-emails', eventId, null]
 *              and the optimistic Mark Confirmed mutation
 * @idempotent yes - multiple mounts share the cache entry
 */
export function Confirmations() {
  const { eventId, event } = useEvent();
  const [tab, setTab] = useState<Tab>('pending');
  const {
    data: emails,
    isPending,
    error,
  } = useTrackedEmails({
    eventId: eventId ?? undefined,
  });
  const markConfirmed = useMarkConfirmed();
  const me = useMe();
  const isAdmin = me.data?.role === 'admin';
  const gmailPoll = useGmailPoll();

  useSetTopbar({
    title: 'Confirmations',
    subtitle: event ? `${event.code} - ${event.name}` : undefined,
  });

  const filtered = useMemo(() => {
    const all = emails ?? [];
    // Phase 6.3 D-POLICY-03: IN_CONVERSATION rows surface in the Pending
    // tab alongside PENDING so reviewers see ambiguous classifications
    // in their natural triage queue.
    if (tab === 'pending')
      return all.filter((e) => e.status === 'PENDING' || e.status === 'IN_CONVERSATION');
    if (tab === 'confirmed') return all.filter((e) => e.status === 'CONFIRMED');
    return all;
  }, [emails, tab]);

  const pendingCount = useMemo(
    () =>
      (emails ?? []).filter((e) => e.status === 'PENDING' || e.status === 'IN_CONVERSATION').length,
    [emails],
  );

  if (isPending) {
    return (
      <div className="max-w-[1100px] flex flex-col gap-2.5">
        <Skeleton variant="card" />
        <Skeleton variant="card" />
        <Skeleton variant="card" />
      </div>
    );
  }

  if (error) {
    return (
      <Empty
        icon={<Icon name="alert" size={20} />}
        title="Couldn't load confirmations"
        description={error instanceof Error ? error.message : 'Unknown error'}
      />
    );
  }

  const onMarkConfirmed = (id: string) => {
    markConfirmed.mutate(id);
  };

  return (
    <div className="max-w-[1100px]">
      {isAdmin && (
        <div className="mb-4 rounded-lg border border-border-strong bg-bg-muted p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="text-[12px] text-text-muted">
              Testing — run the Gmail reply-poll now (scans the last 24h; no need to wait for the
              5-min cron).
            </div>
            <Button
              size="sm"
              variant="default"
              onClick={() => gmailPoll.mutate(1440)}
              disabled={gmailPoll.isPending}
            >
              <Icon name="retry" size={12} />
              {gmailPoll.isPending ? 'Checking…' : 'Check for replies now'}
            </Button>
          </div>
          {gmailPoll.data && (
            <div className="mt-2 text-[11px] text-text-subtle tabular-nums">
              Last poll: {gmailPoll.data.users} reviewer{gmailPoll.data.users === 1 ? '' : 's'} ·
              fetched {gmailPoll.data.fetched} · processed {gmailPoll.data.processed}
              {Object.entries(gmailPoll.data.outcomes)
                .map(([k, v]) => ` · ${k}=${v}`)
                .join('')}
              {gmailPoll.data.fetched === 0 &&
                ' — 0 replies fetched (is the reviewer Gmail connected, and did the sponsor reply in-thread?)'}
            </div>
          )}
        </div>
      )}
      <Tabs value={tab} onValueChange={(v) => setTab(v as Tab)} className="mb-5">
        <TabsList>
          <TabsTrigger value="pending">
            Pending
            <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full px-1.5 text-[10px] font-semibold bg-bg-muted text-text-muted">
              {pendingCount}
            </span>
          </TabsTrigger>
          <TabsTrigger value="confirmed">Confirmed</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>

        <TabsContent value={tab}>
          {filtered.length === 0 ? (
            <Empty
              icon={<Icon name="confirm" size={20} />}
              title={
                tab === 'pending'
                  ? 'No pending confirmations'
                  : tab === 'confirmed'
                    ? 'No confirmed emails yet'
                    : 'No tracked emails for this event'
              }
              description="Tracked emails appear here when a workflow sends one and waits for the recipient to confirm receipt."
            />
          ) : (
            <div className="flex flex-col gap-2.5">
              {filtered.map((e) => (
                <ConfirmationRow
                  key={e.id}
                  email={e}
                  onMarkConfirmed={onMarkConfirmed}
                  isMarkingId={
                    markConfirmed.isPending && markConfirmed.variables === e.id ? e.id : undefined
                  }
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

/**
 * Renders a single tracked-email row. Pulls the company name from the
 * embedded `workflow.displayName` projection (server includes it on
 * GET /api/tracked-emails - see web/src/api/types.ts).
 *
 * Pulse halo only on PENDING-or-IN_CONVERSATION-and-not-overdue rows:
 * once a row is overdue the pulse becomes visual noise (and the red
 * copy carries the urgency). IN_CONVERSATION rows (Phase 6.3 D-POLICY-03)
 * render an amber "Needs Manual Review" badge plus a "Classifier: ..."
 * caption pulled from `lastClassification.reason`; the Mark-confirmed
 * button stays visible as the manual-fallback path per the locked
 * decision policy.
 */
function ConfirmationRow({
  email: e,
  onMarkConfirmed,
  isMarkingId,
}: {
  email: TrackedEmail;
  onMarkConfirmed: (id: string) => void;
  isMarkingId?: string;
}) {
  const isPending = e.status === 'PENDING';
  const isConfirmed = e.status === 'CONFIRMED';
  // Phase 6.3 D-POLICY-03: IN_CONVERSATION is the "ambiguous classifier
  // result — needs human review" state. Surface it next to PENDING in
  // every visual affordance (pulse halo, overdue check, Mark-confirmed
  // button) because both are awaiting human action.
  const isInConversation = e.status === 'IN_CONVERSATION';
  const overdue = !!(
    (isPending || isInConversation) &&
    e.followUpDueAt &&
    isAfter(new Date(), new Date(e.followUpDueAt))
  );
  // Narrow the JSONB lastClassification to the reason caption shape used
  // by the IN_CONVERSATION badge. The Prisma column is Json? so the
  // wire type is `Record<string, unknown> | null`; only render the
  // caption when `reason` is a non-empty string.
  const classifierReason =
    isInConversation &&
    e.lastClassification &&
    typeof (e.lastClassification as { reason?: unknown }).reason === 'string'
      ? (e.lastClassification as { reason: string }).reason
      : null;
  const sentRel = formatDistanceToNow(new Date(e.createdAt), { addSuffix: true });
  const company = e.workflow?.displayName ?? '(unknown workflow)';
  // stepId encodes the email kind (welcome_email, ops_next_steps, ...).
  // Render it humanized so the row reads like the design handoff sample.
  const kindLabel = titleCase(e.stepId);

  return (
    <RowCard style={{ cursor: 'default' }}>
      <Dot
        status={isPending || isInConversation ? 'waiting' : 'completed'}
        pulse={(isPending || isInConversation) && !overdue}
        size={12}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[14px] font-semibold truncate">{company}</span>
          <span className="text-[12px] text-text-subtle">{'·'}</span>
          <span className="text-[12px] text-text-muted truncate">{kindLabel}</span>
          {isInConversation && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-waiting-soft text-waiting border border-waiting-border">
              Needs Manual Review
            </span>
          )}
          {e.status === 'CLOSED' && (
            <span
              className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-bg-soft text-text-muted border border-border"
              title="Cleared when the event was closed out — no longer awaiting a reply"
            >
              Closed out
            </span>
          )}
        </div>
        <div className="text-[12px] text-text-muted truncate mt-0.5">
          <span>To: {e.recipientEmail}</span>
          <span className="text-text-faint mx-1.5">{'·'}</span>
          <span>Subject: {e.subject}</span>
        </div>
        {classifierReason && (
          <div className="mt-1 text-[11px] text-text-muted truncate" title={classifierReason}>
            Classifier: {classifierReason}
          </div>
        )}
        {overdue && e.followUpDueAt && (
          <div className="mt-1.5 text-[12px] text-failed font-medium">
            <Icon name="alert" size={11} className="inline mr-1" />
            Overdue - followup was due{' '}
            {formatDistanceToNow(new Date(e.followUpDueAt), { addSuffix: true })}
          </div>
        )}
        {isConfirmed && e.confirmedAt && (
          <div className="mt-1 text-[12px] text-text-muted">
            Confirmed {format(new Date(e.confirmedAt), 'MMM d, yyyy h:mm a')}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5 items-end shrink-0">
        <span className="text-[11px] text-text-subtle tabular-nums">{sentRel}</span>
        {(isPending || isInConversation) && (
          <Button
            size="sm"
            variant="primary"
            onClick={() => onMarkConfirmed(e.id)}
            disabled={!!isMarkingId}
          >
            <Icon name="check" size={12} />
            Mark confirmed
          </Button>
        )}
      </div>
    </RowCard>
  );
}
